import React, { useEffect, useRef } from "react"
import {
    View,
    StyleSheet,
    Text,
    TouchableOpacity,
    Animated,
    Easing,
} from "react-native"
import { LinearGradient } from "expo-linear-gradient"
import { theme } from "@common/theme/theme"
import { SUBJECT_GOALS, goalIndexOf } from "./funnelSubjectGoals"
import FunnelGoalBadge from "./FunnelGoalBadge"

/**
 * Le curseur d'objectif d'UNE matière — la nouveauté produit du Week-end
 * Réussite, et le moment où on vend du rêve.
 *
 * On demande au parent où il veut aller sur cette matière-là, pas où l'élève en
 * est : un même élève peut vouloir débloquer les maths et viser l'excellence en
 * français. Quatre crans, parce qu'un parent n'a pas l'information pour en
 * placer huit, et parce que chaque cran doit décrire un travail distinct.
 *
 * Le parti pris visuel : le cran choisi est une **carte qui s'allume**, pas une
 * ligne de formulaire. La barre se remplit d'un dégradé qui va du bleu de la
 * marque à l'or de l'excellence, les étoiles se remplissent une par une, et la
 * promesse change avec un fondu. Monter en ambition doit se sentir.
 *
 * Aucun cran n'est humiliant : « Déclic » s'allume aussi fièrement qu'« Expert ».
 */
const FunnelGoalDial = ({ subjectName, value, onChange }) => {
    const index = goalIndexOf(value)
    const goal = SUBJECT_GOALS[index]
    const last = SUBJECT_GOALS.length - 1

    // La valeur reste la source de vérité ; l'animation ne fait que la suivre.
    const slide = useRef(new Animated.Value(index)).current
    const pop = useRef(new Animated.Value(1)).current
    const fade = useRef(new Animated.Value(1)).current

    useEffect(() => {
        Animated.parallel([
            Animated.spring(slide, {
                toValue: index,
                friction: 8,
                tension: 90,
                useNativeDriver: false,
            }),
            Animated.sequence([
                Animated.timing(pop, {
                    toValue: 1.12,
                    duration: 120,
                    easing: Easing.out(Easing.quad),
                    useNativeDriver: true,
                }),
                Animated.spring(pop, {
                    toValue: 1,
                    friction: 4,
                    tension: 150,
                    useNativeDriver: true,
                }),
            ]),
            Animated.sequence([
                Animated.timing(fade, {
                    toValue: 0,
                    duration: 90,
                    useNativeDriver: true,
                }),
                Animated.timing(fade, {
                    toValue: 1,
                    duration: 220,
                    easing: Easing.out(Easing.cubic),
                    useNativeDriver: true,
                }),
            ]),
        ]).start()
    }, [index])

    const fillWidth = slide.interpolate({
        inputRange: [0, last],
        outputRange: ["14%", "100%"],
    })

    // Plus on vise haut, plus la carte passe au vert.
    //
    // Vert et non or : l'orange du thème est une couleur d'AVERTISSEMENT, mal
    // contrastée en aplat et étrangère à l'identité bleue d'où viennent les
    // parents. Le vert dit « progression validée », ce qui est exactement le
    // sens d'un cran haut.
    const isHigh = index >= 2

    return (
        <View style={[styles.card, isHigh && styles.cardHigh]}>
            <View style={styles.head}>
                <Text style={styles.subject} numberOfLines={1}>
                    {subjectName}
                </Text>
                <Animated.View style={{ transform: [{ scale: pop }] }}>
                    <FunnelGoalBadge goal={goal} />
                </Animated.View>
            </View>

            <Animated.Text
                style={[
                    styles.promise,
                    isHigh && styles.promiseHigh,
                    { opacity: fade },
                ]}
            >
                {goal.promise}
            </Animated.Text>

            <View style={styles.trackWrap}>
                <View style={styles.track}>
                    <Animated.View
                        style={[styles.fillWrap, { width: fillWidth }]}
                    >
                        <LinearGradient
                            colors={[
                                theme.colors.primary,
                                isHigh
                                    ? theme.colors.success
                                    : theme.colors.primary,
                            ]}
                            start={{ x: 0, y: 0.5 }}
                            end={{ x: 1, y: 0.5 }}
                            style={styles.fill}
                        />
                    </Animated.View>
                </View>
                <View style={styles.notches}>
                    {SUBJECT_GOALS.map((g, i) => (
                        <TouchableOpacity
                            key={g.slug}
                            style={styles.notch}
                            activeOpacity={0.6}
                            onPress={() => onChange(g.slug)}
                            accessibilityRole="radio"
                            accessibilityState={{ selected: i === index }}
                            accessibilityLabel={`${g.name} — ${g.promise}`}
                        >
                            <View
                                style={[
                                    styles.dot,
                                    i <= index && styles.dotOn,
                                    i === index && styles.dotCurrent,
                                    i === index &&
                                        isHigh &&
                                        styles.dotCurrentHigh,
                                ]}
                            />
                            <Text
                                style={[
                                    styles.notchLabel,
                                    // Sur la carte dorée, le gris clair des
                                    // crans inactifs disparaît dans le fond :
                                    // ils passent en brun lisible.
                                    isHigh && styles.notchLabelOnGold,
                                    i === index && styles.notchLabelOn,
                                    i === index &&
                                        isHigh &&
                                        styles.notchLabelHigh,
                                ]}
                                numberOfLines={1}
                            >
                                {g.name}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>
            </View>
        </View>
    )
}

const TRACK_H = 7

const styles = StyleSheet.create({
    card: {
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 14,
        paddingTop: 13,
        paddingBottom: 4,
        marginBottom: 10,
    },
    // Viser haut se voit avant même de lire : la carte prend la teinte de l'or.
    cardHigh: {
        borderColor: theme.colors.success,
        backgroundColor: theme.colors.successLight,
    },
    head: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
    },
    subject: {
        flexShrink: 1,
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    promise: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.grey,
        marginTop: 5,
    },
    promiseHigh: { color: theme.colors.successDark },
    trackWrap: { marginTop: 16 },
    track: {
        height: TRACK_H,
        borderRadius: TRACK_H,
        backgroundColor: theme.colors.lightMore,
        overflow: "hidden",
    },
    fillWrap: {
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        borderRadius: TRACK_H,
        overflow: "hidden",
    },
    fill: { flex: 1 },
    notches: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: -10,
    },
    // Cible tactile large autour d'un point volontairement petit.
    notch: { flex: 1, alignItems: "center", paddingBottom: 8 },
    dot: {
        width: 13,
        height: 13,
        borderRadius: 13,
        backgroundColor: theme.colors.white,
        borderWidth: 2.5,
        borderColor: theme.colors.lightMore,
    },
    dotOn: { borderColor: theme.colors.primary },
    dotCurrent: {
        width: 19,
        height: 19,
        borderRadius: 19,
        borderWidth: 5,
        marginTop: -3,
        borderColor: theme.colors.primary,
    },
    dotCurrentHigh: { borderColor: theme.colors.successDark },
    notchLabel: {
        marginTop: 6,
        fontFamily: "Montserrat-Medium",
        fontSize: 10.5,
        color: theme.colors.lightMore,
    },
    notchLabelOnGold: { color: "rgba(15,90,72,0.5)" },
    notchLabelOn: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
    },
    notchLabelHigh: { color: theme.colors.successDark },
})

export default FunnelGoalDial
